/* eslint-disable no-console */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import store from 'store';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import Box from 'ui-box';
import moment from 'moment';
import { Button } from 'antd';
import InputField from '../form-helpers/InputField';
import PhoneInputField from '../form-helpers/PhoneInputField';
import InputSelectField from '../form-helpers/InputSelectField';
import { FormDatePicker } from '../form-helpers/FormField';
import LoadingSpinner from '../../common/LoadingSpinner';
import { errorNotification } from '../../../utils/notification-helpers';
import { listProvinces } from '../../../api';
import { putPatienAnonymousAPICall, putPatientAuth0APICall } from '../../../api/patient';
import { LOCAL_STORAGE_AUTH_KEY } from '../../../config/constants';
import { simplifyPhoneNumber } from '../../../routes/helpers';

const formItemLayout = {
  labelCol: {
    span: 24,
  },
  wrapperCol: {
    span: 24,
  },
}

function PersonalnformationForm({ onSubmitBtnText }) {
  const { t } = useTranslation(['common', 'user-account']);
  const profileGuest = store.get(LOCAL_STORAGE_AUTH_KEY.USER_PROFILE_GUEST);
  const history = useHistory();

  const FormSchema = useMemo(
    () =>
      Yup.object().shape({
        firstName: Yup.string().required(t('form.error.required')),
        lastName: Yup.string().required(t('form.error.required')),
        dob: Yup.string().required(t('form.error.required')),
        province: Yup.string().required(t('form.error.required')),
        mobile: Yup.string().required(t('form.error.required')),
      }),
    [t]
  );

  const initialValues = {
    firstName: '',
    lastName: '',
    dob: '',
    province: '',
    mobile: profileGuest.mobile !== null ? profileGuest.mobile : '',
  };

  return (
    <div style={{ width: '100%', textAlign: 'left' }}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={FormSchema}
        onSubmit={async (values) => {
          const dataForm = { ...values };
          dataForm.dob = moment(dataForm.dob).format('YYYY-MM-DD')
          dataForm.name = `${dataForm.firstName} ${dataForm.lastName}`

          if (profileGuest.email.length === 0 && (profileGuest.mobile !== null || values.mobile)) {
            dataForm.email = `${simplifyPhoneNumber(profileGuest.mobile ?? values.mobile)}@alemhealth.com`
          } else {
            dataForm.email = profileGuest.email
          }

          const { responseOk } = await putPatienAnonymousAPICall(dataForm);
          if (responseOk) {
            const bodyAuth0 = {
              email: dataForm.email,
              password: profileGuest.password,
              name: dataForm.name
            }
            const result = await putPatientAuth0APICall(bodyAuth0);
            const checkOk = result.responseOk
            if (checkOk) {
              store.set(LOCAL_STORAGE_AUTH_KEY.USER_PROFILE_SIGNUP, {...profileGuest, ...dataForm})
              store.set(LOCAL_STORAGE_AUTH_KEY.USER_PROFILE_GUEST, {...profileGuest, ...dataForm})
              history.push('/sign-up/guest/location-access-request');
            }
          } else {
            errorNotification(t('form.error.submit'));
          }
        }}
      >
        {({ isSubmitting, isValid, setFieldValue }) => {
          return (
            <Form>
              <fieldset disabled={isSubmitting}>
                <InputField
                  type="text"
                  name="firstName"
                  label={t('user-account:personalInformation.form.firstName.label')}
                  placeholder={t('user-account:personalInformation.form.firstName.placeholder')}
                />
                <InputField
                  type="text"
                  name="lastName"
                  label={t('user-account:personalInformation.form.lastName.label')}
                  placeholder={t('user-account:personalInformation.form.lastName.placeholder')}
                />
                <PhoneInputField
                  name="mobile"
                  label={t('user-account:personalInformation.form.phoneNumber.label')}
                  placeholder={t('user-account:personalInformation.form.phoneNumber.placeholder')}
                />
                <FormDatePicker
                  type="date"
                  name="dob"
                  defaultValue={moment()}
                  format="YYYY/MM/DD"
                  style={{ width: '100%', padding: '3%' }}
                  formItemLayout={formItemLayout}
                  onChange={(e) => setFieldValue('dob', e)}
                  label={t('user-account:personalInformation.form.dateBirth.label')}
                  placeholder={t('user-account:personalInformation.form.dateBirth.placeholder')}
                  disabledDate={current => {
                    return current && current > moment();
                  }}
                />
                <InputSelectField
                  name="province"
                  label={t('user-account:personalInformation.form.province.label')}
                  placeholder={t('user-account:personalInformation.form.province.placeholder')}
                  style={{ width: '100%', padding: '3%' }}
                  formItemLayout={formItemLayout}
                  options={listProvinces()}
                />
                <Box paddingTop={15}>
                  <Button
                    disabled={isSubmitting || !isValid}
                    loading={isSubmitting}
                    htmlType="submit"
                    type="primary"
                    size="large"
                    block
                  >
                    {isSubmitting ? <LoadingSpinner position="full" transparent={false} /> : onSubmitBtnText}
                  </Button>
                </Box>
              </fieldset>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}

PersonalnformationForm.defaultProps = {
  onSubmitBtnText: 'Confirm & Save Changes',
};

const { string } = PropTypes;

PersonalnformationForm.propTypes = {
  onSubmitBtnText: string,
};

export default PersonalnformationForm;
